import { LitElement, html } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';

import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '@Root/lib/lit-styles.js';
import { SHARED_CLASSES, SHARED_ELEMENTS } from './styles.js';
import { imagebossUrl } from '../../helpers/imageboss';
import { debounce } from '../../helpers/debounce';
import {
  analyticsCtaDataObject,
  analyticsLabelFromString,
  fireAdobeAnalyticsEvent,
  handleAdobeNavEvent,
  handleFileDownloadEvent,
  handleMediaItemClick,
} from '../../helpers/adobe-analytics';
import { withLineBreaks } from '../../helpers/text';

// Containers that render CMS rich text through unsafeHTML. No block puts a
// template anchor inside one, so an anchor there was written by an editor.
const RICH_TEXT_SELECTOR = [
  '.description',
  '.block-description',
  '.image-description',
  '.card-description',
  '.description-text',
  '.description-body',
  '.lisa-description',
  '.steve-card-description',
  '.hopper-card-tags',
  '.text-section > p',
  '.text-section-body',
  '.text-section-paragraph',
  '.paragraph',
  '.primary-text',
  '.row-content-text',
  '.william-entry-value',
  '.project-detail-value',
  '.carousel-caption',
].join(', ');

// Rich-text links count as downloads the way CTAs do: by file extension, or
// a tech-specs URL.
const FILE_EXTENSION = /\.(pdf|zip|dwg|dxf|rvt|rfa|skp|jpe?g|png|tiff?|docx?|xlsx?|csv)$/i;
const TECH_SPECS_PATH = /^\/tech-specs\/\d+\/?$/;
const OWN_HOSTNAME = /(^|\.)3-form\.com$/i;
// The hold handleAdobeNavEvent gives a link before it navigates.
const NAV_HOLD_MS = 150;

// The innermost anchor of a click, when it sits in CMS rich text. closest()
// stays inside the anchor's own shadow root, so CTA elements never match.
const richTextAnchorFrom = event => {
  const anchor = event
    ?.composedPath()
    .find(node => node instanceof HTMLAnchorElement);
  return anchor?.parentElement?.closest(RICH_TEXT_SELECTOR) ? anchor : null;
};

const isSamePageHash = anchor =>
  !!anchor.hash &&
  anchor.href.split('#')[0] === window.location.href.split('#')[0];

// A plain left click that replaces this page in its own tab. Modified
// clicks, other targets, mailto/tel and in-page hashes never unload it.
const unloadsPage = (event, anchor) =>
  event.button === 0 &&
  !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) &&
  ['', '_self'].includes(anchor.target) &&
  !anchor.hasAttribute('download') &&
  /^https?:$/.test(anchor.protocol) &&
  !isSamePageHash(anchor);

export class Lit3formBlock extends LitElement {
  static defineElement() {
    customElements.define(`${this.characterName}-block`, this);
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      ...this.characterStyles,
    ];
  }

  static get characterStyles() {
    return [];
  }

  static get properties() {
    return {
      content: { type: Object },
      cart: { type: Object },
      _useMobileLayout: { type: Boolean },
      ...this.characterProperties,
    };
  }

  static get characterProperties() {
    return {};
  }

  constructor() {
    super();

    this.content = JSON.parse(this.getAttribute('content'));
    this._useMobileLayout = !window.matchMedia('(min-width: 600px)').matches;
    this.addEventListener('click', event => this.sendRichTextLinkEvent(event));
  }

  connectedCallback() {
    super.connectedCallback();

    this.removePlaceholderDiv();
    this._updateIsLastBlock();
    window.addEventListener(
      'resize',
      debounce(this._handleMobileTransition, 50)
    );
  }

  _updateIsLastBlock() {
    const blocks = Array.from(document.querySelectorAll('*')).filter(el =>
      el.tagName.toLowerCase().endsWith('-block')
    );
    const isLast = blocks[blocks.length - 1] === this;
    this.classList.toggle('is-last-block', isLast);
  }

  _handleMobileTransition() {
    const useMobile = !window.matchMedia('(min-width: 600px)').matches;
    if (this._useMobileLayout !== useMobile) {
      this._useMobileLayout = useMobile;
      const useMobileEvent = new CustomEvent('useMobileEvent', {
        detail: {
          useMobile,
        },
        bubbles: true,
        composed: true,
      });
      window.dispatchEvent(useMobileEvent);
    }
  }

  removePlaceholderDiv() {
    const el = document.querySelector(
      `.js-${this.characterName}-block-${this.content.id}`
    );
    if (!!el) el.remove();
  }

  get characterName() {
    return this.constructor.characterName;
  }

  get horizontalRule() {
    return this.hideSucceedingHorizontalRule
      ? html``
      : html`
          <div
            class="horizontal-rule print-none"
            style="${styleMap(this.horizontalRuleStyles)}"
          ></div>
        `;
  }

  get horizontalRuleStyles() {
    return {
      'border-bottom-width': this.hideSucceedingHorizontalRule ? '0' : '1px',
    };
  }

  get hideSucceedingHorizontalRule() {
    return this.content.hide_succeeding_horizontal_rule;
  }

  get addToCartQuantity() {
    return 1;
  }

  imagebossUrl(imageUrl, width, height) {
    return imagebossUrl(imageUrl, { width, height });
  }

  withLineBreaks(string) {
    return withLineBreaks(string);
  }

  // Rich-text fields can carry links, and several blocks wrap a description
  // in the card's own <a>. The nested anchor survives, so a click on it also
  // reaches the card handler and would report the card's destination. Blocks
  // whose card is a link guard their click handler with this.
  richTextLinkClicked(event) {
    return !!richTextAnchorFrom(event);
  }

  // Editor links have no handler of their own, so the block reports them.
  sendRichTextLinkEvent(event) {
    const anchor = richTextAnchorFrom(event);
    // A cancelled click was handled, and reported, by whatever cancelled it.
    if (!anchor || !anchor.href || event.defaultPrevented) return;
    const block = event
      .composedPath()
      .find(node => node instanceof Lit3formBlock);
    if (block !== this) return;

    const placement = analyticsLabelFromString(this.analyticsBlockLabel);
    const name = analyticsLabelFromString(
      anchor.textContent.trim() || anchor.getAttribute('href')
    );
    const extension = (anchor.pathname.match(FILE_EXTENSION) || [])[1];

    if (extension || TECH_SPECS_PATH.test(anchor.pathname)) {
      const fileData = {
        file_format: extension ? extension.toUpperCase() : '',
        text: name,
        link_url: anchor.href,
        isDownload: true,
      };
      handleFileDownloadEvent(analyticsCtaDataObject(fileData, placement));
      return;
    }

    const offSite =
      /^https?:$/.test(anchor.protocol) &&
      anchor.hostname !== window.location.hostname &&
      !OWN_HOSTNAME.test(anchor.hostname);
    const navString = `${offSite ? 'EXIT' : 'NAV'}|${placement}|${name}`;
    const detail = offSite
      ? { eVar8: navString, eVar108: anchor.href }
      : { eVar8: navString };

    if (!unloadsPage(event, anchor)) {
      fireAdobeAnalyticsEvent({ type: 'click', detail });
      return;
    }

    // Launch sends the hit with fetch, which an unload can cut off, so a
    // click that leaves the page is held and replayed like other nav links.
    event.preventDefault();
    if (offSite) {
      // handleAdobeNavEvent would move it to a new tab the visitor did not
      // ask for, and Safari can popup-block a delayed window.open.
      fireAdobeAnalyticsEvent({ type: 'click', detail });
      setTimeout(() => {
        window.location.href = anchor.href;
      }, NAV_HOLD_MS);
      return;
    }

    // A relative URL keeps same-origin links in the tab on any host.
    handleAdobeNavEvent({
      navString,
      destinationUrl:
        anchor.origin === window.location.origin
          ? anchor.pathname + anchor.search + anchor.hash
          : anchor.href,
      event,
    });
  }

  get headerText() {
    return this.withLineBreaks(this.content.header) || '';
  }

  get descriptionText() {
    return this.withLineBreaks(this.content.description) || '';
  }

  get eyebrowText() {
    return this.withLineBreaks(this.content.eyebrow) || '';
  }

  get imagesArr() {
    return this.content.images || [];
  }

  // Placement label for analytics strings; blocks with a non-header placement
  // override this.
  get analyticsBlockLabel() {
    return (
      this.headerText ||
      this.characterName.charAt(0).toUpperCase() + this.characterName.slice(1)
    );
  }

  // Image/driver click: navigate when the item has a url, otherwise treat the
  // click as a full-resolution image download.
  handleMediaClick(item, event) {
    handleMediaItemClick(item, event, {
      navPlacement: this.analyticsBlockLabel,
      downloadPlacement: this.headerText,
    });
  }
}

export * from 'lit';
export { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
export { ifDefined } from 'lit/directives/if-defined.js';
export { classMap } from 'lit/directives/class-map.js';

export * from './icons';

export * from './components/block-intro';
export * from './components/cta-button';
export * from './components/cta-text';
export * from './components/driver-card';
export * from './components/image-caption';
export * from './components/info-tooltip';
export * from './components/cta-text-with-icon';
export * from './components/download-button';
export * from './components/styled-multi-select';
export * from './components/dialog-modal';
